/**
 * HC About page behaviour.
 *
 * The bands of the About page each used to ship their own copy of this script.
 * They are blocks now, so the two behaviours they share live here once and are
 * driven entirely by data attributes:
 *
 *   [data-hcab-reveal]        fades in when scrolled into view
 *   [data-hcab-acc]           an accordion group (one item open at a time)
 *   [data-hcab-acc-item]      an item inside that group
 *   [data-hcab-acc-open-first] opens the group's first item on load
 *   [data-hcab-acc-skip]      an item that is decoration, not a toggle
 *
 * Everything is idempotent: bands are re-initialised on shopify:section:load
 * without double-binding, because bound nodes are flagged.
 */
(function () {
  'use strict';

  var BOUND = 'hcabBound';

  /**
   * HC a11y: the expanded state lives on the item's real <button> trigger,
   * not on the card. The card used to carry role="button", which makes its
   * children presentational - that stripped every era/staff heading out of
   * the accessibility tree and gave the control an accessible name built
   * from the whole card's text. Cards are now plain containers that stay
   * mouse-clickable; the button is what AT sees and what keyboards reach.
   */
  function setExpanded(item, expanded) {
    var trigger = item.querySelector('[data-hcab-acc-trigger]');
    (trigger || item).setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function openItem(group, item) {
    var items = group.querySelectorAll('[data-hcab-acc-item]');

    for (var i = 0; i < items.length; i++) {
      items[i].classList.remove('on');
      setExpanded(items[i], false);
    }

    if (item) {
      item.classList.add('on');
      setExpanded(item, true);
    }
  }

  function initAccordion(group) {
    if (group.dataset[BOUND] === 'acc') return;
    group.dataset[BOUND] = 'acc';

    var items = group.querySelectorAll('[data-hcab-acc-item]');

    for (var i = 0; i < items.length; i++) {
      (function (item) {
        if (item.hasAttribute('data-hcab-acc-skip')) return;

        function toggle() {
          // Clicking the open item closes it, so a group can rest fully closed.
          var wasOpen = item.classList.contains('on');
          openItem(group, wasOpen ? null : item);
        }

        item.addEventListener('click', function (event) {
          // A link inside an open panel must navigate, not collapse it.
          if (event.target.closest && event.target.closest('a')) return;
          toggle();
        });

        // No keydown shim: the trigger is a real <button>, so Enter and Space
        // fire a native click that bubbles to the handler above. The card
        // itself is no longer focusable, so a shim here could never run.

      })(items[i]);
    }

    if (group.hasAttribute('data-hcab-acc-open-first')) {
      for (var j = 0; j < items.length; j++) {
        if (!items[j].hasAttribute('data-hcab-acc-skip')) {
          openItem(group, items[j]);
          break;
        }
      }
    }
  }

  var observer = null;

  function revealObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Reveal when it comes into view, but ALSO when it has already gone past
          // the top. Scrolling fast, jumping to an anchor, hitting End, or restoring
          // a scroll position all move the viewport further than one callback can
          // track, and anything skipped that way used to stay at opacity 0 forever -
          // permanently invisible content, not a missed animation.
          if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return;
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        });
      },
      // Start slightly before the element arrives so the fade lands as it enters.
      { threshold: 0.01, rootMargin: '0px 0px -40px 0px' }
    );

    return observer;
  }

  function initReveal(root) {
    var targets = root.querySelectorAll('[data-hcab-reveal]');
    var obs = revealObserver();

    for (var i = 0; i < targets.length; i++) {
      if (targets[i].dataset[BOUND] === 'reveal') continue;
      targets[i].dataset[BOUND] = 'reveal';

      if (obs) {
        obs.observe(targets[i]);
      } else {
        targets[i].classList.add('in');
      }
    }
  }

  /**
   * [data-hcab-carousel]: a track that scrolls sideways ([data-hcab-carousel-track])
   * with optional [data-hcab-carousel-prev] / [data-hcab-carousel-next] buttons.
   * The track scrolls and snaps in CSS, so swipe, trackpad and keyboard work
   * without this script. The buttons move a full row of cards, or one card when
   * data-hcab-carousel-step="card". Positions come from the first card's width
   * and scrollLeft, never from rects. When every card fits, the band gets
   * .is-static and its CSS hides the buttons.
   */
  function initCarousel(root) {
    if (root.dataset[BOUND] === 'carousel') return;

    var track = root.querySelector('[data-hcab-carousel-track]');
    if (!track) return;
    root.dataset[BOUND] = 'carousel';

    var prev = root.querySelector('[data-hcab-carousel-prev]');
    var next = root.querySelector('[data-hcab-carousel-next]');
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function stride() {
      var first = track.firstElementChild;
      if (!first) return 0;
      return first.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      var moves = max > 4;
      root.classList.toggle('is-static', !moves);
      if (prev) prev.disabled = !moves || track.scrollLeft < 4;
      if (next) next.disabled = !moves || track.scrollLeft > max - 4;
    }

    function go(direction) {
      var width = stride();
      if (!width) return;
      var step = root.getAttribute('data-hcab-carousel-step') === 'card' ? 1 : Math.max(1, Math.floor((track.clientWidth + 4) / width));
      var index = Math.round(track.scrollLeft / width) + step * direction;
      track.scrollTo({ left: Math.max(0, index * width), behavior: still ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    window.addEventListener('load', sync);
    sync();
  }

  function init(scope) {
    var root = scope || document;
    var groups = root.querySelectorAll('[data-hcab-acc]');

    for (var i = 0; i < groups.length; i++) initAccordion(groups[i]);

    var carousels = root.querySelectorAll('[data-hcab-carousel]');

    for (var c = 0; c < carousels.length; c++) initCarousel(carousels[c]);

    initReveal(root);
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    init();
  });

  // Restoring from bfcache re-runs no scripts and fires no intersections, so a
  // page returned to via Back could come up with whole bands still hidden.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('[data-hcab-reveal]').forEach(function (el) {
      el.classList.add('in');
    });
  });

  // Last resort: nothing on this page may stay invisible. If an element is still
  // unrevealed once the page has settled and it is at or above the fold, show it.
  window.addEventListener('load', function () {
    setTimeout(function () {
      document.querySelectorAll('[data-hcab-reveal]:not(.in)').forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('in');
      });
    }, 1200);
  });

  // Theme editor: a re-rendered section arrives with fresh, unbound nodes.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });

  // Theme editor: selecting a block in the sidebar should open and show it.
  document.addEventListener('shopify:block:select', function (event) {
    var id = event.detail && event.detail.blockId;
    var target = event.target;

    if (!target && id) {
      target = document.querySelector('[data-shopify-editor-block-id="' + id + '"]');
    }

    if (!target || !target.closest) return;

    var item = target.closest('[data-hcab-acc-item]');

    if (item && !item.hasAttribute('data-hcab-acc-skip')) {
      var group = item.closest('[data-hcab-acc]');
      if (group) openItem(group, item);
    }

    var scrollTo = item || target;
    if (scrollTo.scrollIntoView) {
      scrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
})();
